import { IF, nil, NO, UIRectangle, UITextView, UIView, YES } from "uicore-ts"
import { RHColors, RHFont, RHSpacing } from "./Utils_slop/RHDesignTokens"
import { RHProcurementDetailsViewTokens } from "./Utils_slop/RHViewTokens"


/**
 * RHLabeledRow — a single "label : value" detail row that collapses responsively.
 *
 * Above `splitBreakpoint` it lays the header out in a fixed-width left column with
 * the value in the right column (both carrying a bottom divider); below it, header
 * and value stack vertically (only the value keeps the divider). An optional
 * `trailingAccessory` (typically a small button) floats just past the measured
 * value text in split mode and drops onto its own row beneath the value in stacked
 * mode.
 *
 * The value is a slot: pass a plain `UITextView` for ordinary fields, or any
 * container `UIView` (e.g. a stack of sub-rows) for richer values. The row derives
 * its own height via virtual layout, so callers can stack rows with
 * `framesByDistributingViewsAsColumn` without measuring each one by hand.
 *
 * Divider styling is applied inside `layoutSubviews` on purpose: whether the header
 * carries a divider depends on which layout (split vs stacked) is chosen, so it
 * cannot be baked in at configuration time.
 */
export class RHLabeledRow extends UIView {

    readonly headerLabel = new UITextView(undefined, UITextView.type.paragraph)
        .configuredWithObject({
            textAlignment: UITextView.textAlignment.left,
            textColor: RHColors.textPrimary,
            style: {
                fontFamily: RHFont.familyBase,
                fontWeight: RHProcurementDetailsViewTokens.detailHeaderFontWeight,
                letterSpacing: RHProcurementDetailsViewTokens.detailHeaderLetterSpacing,
                lineHeight: RHFont.lineHeightBase
            }
        })
        .addedAsSubviewToView(this)

    readonly _headerDividerView = new UIView()
        .configuredWithObject({ userInteractionEnabled: NO })
        .addedAsSubviewToView(this)

    readonly _valueDividerView = new UIView()
        .configuredWithObject({ userInteractionEnabled: NO })
        .addedAsSubviewToView(this)

    _valueView?: UIView
    _trailingAccessory?: UIView

    // ── Layout configuration ──────────────────────────────────────────────────

    labelColumnWidth: number = RHProcurementDetailsViewTokens.detailLabelColumnWidth
    splitBreakpoint: number = RHProcurementDetailsViewTokens.detailSplitLayoutBreakpoint
    minHeight: number = RHProcurementDetailsViewTokens.detailRowMinHeight
    columnGap: number = RHSpacing.large
    stackedRowGap: number = RHSpacing.tiny

    /**
     * When YES the value is forced to at least `minHeight` in stacked mode. Set for
     * container-valued rows (parts, CPV, protocol) so a sparse value still reads as
     * a full row; leave NO for plain text values, which hug their content.
     */
    valueFillsMinHeight = NO

    // Trailing accessory geometry — only consulted when `trailingAccessory` is set.
    accessoryWidth = 0
    accessoryHeight = 0
    accessoryGap: number = RHSpacing.small
    accessoryStackedTopGap: number = RHSpacing.small

    /**
     * Toggles the bottom dividers. Set to NO when an inline editor opens below this
     * row so the two read as one continuous block.
     */
    showsDividers = YES


    get valueView(): UIView | undefined {
        return this._valueView
    }

    set valueView(view: UIView | undefined) {
        if (this._valueView === view) {
            return
        }
        this._valueView?.removeFromSuperview()
        this._valueView = view
        view?.addedAsSubviewToView(this)
        this.setNeedsLayout()
    }

    /** Convenience accessor for rows whose value is a plain text label. */
    get valueLabel(): UITextView {
        return this._valueView as UITextView
    }

    get trailingAccessory(): UIView | undefined {
        return this._trailingAccessory
    }

    set trailingAccessory(view: UIView | undefined) {
        if (this._trailingAccessory === view) {
            return
        }
        this._trailingAccessory?.removeFromSuperview()
        this._trailingAccessory = view
        view?.addedAsSubviewToView(this)
        this.setNeedsLayout()
    }


    get _dividerBorderBottom(): string {
        return `1px solid ${RHColors.dividerAlpha50.stringValue}`
    }


    override layoutSubviews() {
        super.layoutSubviews()

        const value = this._valueView
        if (!value) {
            return
        }

        const bounds = this.contentBounds
        const divider = IF(this.showsDividers)(() => this._dividerBorderBottom).ELSE(() => "none")

        if (bounds.width >= this.splitBreakpoint) {
            this._layoutSplit(bounds, value, divider)
        }
        else {
            this._layoutStacked(bounds, value, divider)
        }

        const dividerFrame = bounds
            .rectangleWithInsets(0, 0, -this.contentInsets.bottom, 0)
            .rectangleWithHeight(1, 1)
        this._headerDividerView.weakFrame = dividerFrame.rectangleWithWidth(this.headerLabel.frame.width)
        this._valueDividerView.weakFrame = dividerFrame
            .rectangleWithX(value.frame.x)
            .rectangleWithWidth(value.frame.width)
    }


    _layoutSplit(bounds: UIRectangle, value: UIView, divider: string) {

        const labelColumnWidth = this.labelColumnWidth
        const valueColumnWidth = bounds.width - labelColumnWidth - this.columnGap

        const rowHeight = [
            this.minHeight,
            this.headerLabel.intrinsicContentHeight(labelColumnWidth),
            value.intrinsicContentHeight(valueColumnWidth),
            IF(this._trailingAccessory != undefined)(() => this.accessoryHeight).ELSE(() => 0)
        ].max()

        const rowRect = bounds.rectangleWithHeight(rowHeight)
        const [labelColumn, valueColumn] = rowRect.rectanglesBySplittingWidth(
            [1, 1], this.columnGap, [labelColumnWidth, nil]
        )

        this.headerLabel.frame = labelColumn
        this._headerDividerView.configureWithObject({ style: { borderBottom: divider } })

        const accessory = this._trailingAccessory
        if (accessory) {
            // Keep the value at the full column width so its bottom divider runs the
            // whole row; the accessory floats just past the measured value text.
            const valueTextWidth = [
                value.intrinsicContentWidth(valueColumn.width) + RHSpacing.large,
                valueColumn.width - this.accessoryGap - this.accessoryWidth
            ].min()
            value.frame = valueColumn
            accessory.frame = valueColumn
                .rectangleWithWidth(valueTextWidth)
                .rectangleForNextColumn(this.accessoryGap, this.accessoryWidth)
                .rectangleWithHeight(this.accessoryHeight, 0.5)
        }
        else {
            value.frame = valueColumn.rectangleWithHeight(rowHeight)
        }

        this._valueDividerView.configureWithObject({ style: { borderBottom: divider } })
    }


    _layoutStacked(bounds: UIRectangle, value: UIView, divider: string) {

        this.headerLabel.frame = bounds.rectangleWithHeight(this.headerLabel)
        // The header never carries the divider in stacked mode.
        this._headerDividerView.configureWithObject({ style: { borderBottom: "none" } })

        let valueFrame: UIRectangle
        if (this.valueFillsMinHeight) {
            const valueHeight = [this.minHeight, value.intrinsicContentHeight(bounds.width)].max()
            valueFrame = this.headerLabel.frame.rectangleForNextRow(this.stackedRowGap, valueHeight)
        }
        else {
            valueFrame = this.headerLabel.frame.rectangleForNextRow(this.stackedRowGap, value)
        }
        value.frame = valueFrame
        this._valueDividerView.configureWithObject({ style: { borderBottom: divider } })

        const accessory = this._trailingAccessory
        if (accessory) {
            accessory.frame = valueFrame
                .rectangleForNextRow(this.accessoryStackedTopGap, this.accessoryHeight)
                .rectangleWithWidth(this.accessoryWidth)
        }
    }

}